import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "components/common/context-menu/ContextMenuV2";
import { BudgetLineContextMenuProps } from "./BudgetLineContextMenu.types";

/**
 * Right-click menu for the detailed budget grid. When a line is targeted it
 * offers reorder/duplicate/delete plus add; right-clicking empty space falls
 * back to just "Add line". Action availability mirrors the row's hover toolbar.
 */
export const BudgetLineContextMenu = ({
  children,
  activeLine,
  canMoveUp,
  canMoveDown,
  canAddLine,
  structureDisabled = false,
  structureDisabledTitle,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onAddLine,
}: BudgetLineContextMenuProps) => {
  const lockedTitle = structureDisabled ? structureDisabledTitle : undefined;

  return (
    <ContextMenu
      onOpenChange={(open) => {
        // AG Grid keeps DOM focus on the right-clicked cell. When the portaled
        // menu opens, Radix marks the grid `aria-hidden`, which is invalid while
        // a descendant is focused. Blur the cell so focus moves into the menu.
        if (open) {
          (document.activeElement as HTMLElement | null)?.blur();
        }
      }}
    >
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        {activeLine ? (
          <>
            <ContextMenuLabel className="truncate normal-case tracking-normal text-muted-foreground">
              {activeLine.name || "Line"}
            </ContextMenuLabel>
            <ContextMenuItem
              disabled={structureDisabled || !canMoveUp}
              title={lockedTitle}
              onSelect={onMoveUp}
            >
              <ArrowUp />
              Move up
            </ContextMenuItem>
            <ContextMenuItem
              disabled={structureDisabled || !canMoveDown}
              title={lockedTitle}
              onSelect={onMoveDown}
            >
              <ArrowDown />
              Move down
            </ContextMenuItem>
            <ContextMenuItem
              disabled={structureDisabled}
              title={lockedTitle}
              onSelect={onDuplicate}
            >
              <Copy />
              Duplicate
            </ContextMenuItem>
            {canAddLine ? (
              <ContextMenuItem
                disabled={structureDisabled}
                title={lockedTitle}
                onSelect={onAddLine}
              >
                <Plus />
                Add line
              </ContextMenuItem>
            ) : null}
            <ContextMenuSeparator />
            <ContextMenuItem
              variant="destructive"
              disabled={structureDisabled}
              title={lockedTitle}
              onSelect={onDelete}
            >
              <Trash2 />
              Delete
            </ContextMenuItem>
          </>
        ) : (
          <ContextMenuItem
            disabled={structureDisabled || !canAddLine}
            title={lockedTitle}
            onSelect={onAddLine}
          >
            <Plus />
            Add line
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};
